import React, { useState } from 'react';
import { Button, FileInput, Radio, Spinner, Label } from 'flowbite-react';
import { useMutation, useQueryClient } from 'react-query';
import { useUserContext } from 'src/context/UserProvider';
import { useToast } from 'src/context/ToastProvider';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { handleS3Upload } from 'src/utils/imageUpload';
import { get } from 'lodash';
import { optOutOfPlacement } from 'src/apis/application';
import { UserPlacementTags } from 'types';

type OptOutReason = 'already_working' | 'got_placed';

const PlacementOptOutSection = () => {
    const { user } = useUserContext();
    const toast = useToast();
    const queryClient = useQueryClient();
    const [selectedReason, setSelectedReason] = useState<OptOutReason | ''>('');
    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showOptions, setShowOptions] = useState(false);

    // Check if user should see the section
    React.useEffect(() => {
        // Don't show if user is Placed or VerballyPlaced
        if (
            user?.placementStats?.status === UserPlacementTags.Placed ||
            user?.placementStats?.status === UserPlacementTags.VerballyPlaced
        ) {
            return;
        }

        // Don't show if account is deleted
        if (user?.flags?.isDeletedAccount) {
            return;
        }

        // If user has already submitted opt-out, show success message
        if (user?.meta?.placementOptOut) {
            setIsSubmitted(true);
            return;
        }
    }, [user]);

    const optOutMutation = useMutation(['optOutOfPlacement'], optOutOfPlacement, {
        onSuccess() {
            toast({
                title: 'Your request has been submitted successfully.',
                type: 'success',
            });
            setIsSubmitted(true);
            setShowOptions(false);
            queryClient.refetchQueries(['verifyCookie']);
        },
        onError(error: Error) {
            const errorMessage = get(error, ['response', 'data', 'message'], error.message);
            toast({
                title: `Error: ${errorMessage}`,
                type: 'error',
            });
            setIsLoading(false);
        },
    });

    const handleSubmit = async () => {
        if (!selectedReason) {
            toast({
                title: 'Please select a reason',
                type: 'error',
            });
            return;
        }

        if (selectedReason === 'got_placed' && !file) {
            toast({
                title: 'Please upload your offer letter',
                type: 'error',
            });
            return;
        }

        setIsLoading(true);
        let offerLetterUrl = '';

        // Upload offer letter if provided
        if (file && selectedReason === 'got_placed') {
            try {
                const fileSizeMB = file.size / (1024 * 1024);
                if (fileSizeMB > 5) {
                    toast({
                        type: 'error',
                        title: 'File size exceeds the maximum limit of 5 MB',
                        duration: 10000,
                    });
                    setIsLoading(false);
                    return;
                }

                const policy = await getPolicyForS3NonVideo(
                    file.name.trim().split(' ').join('_'),
                    'placementOptOutOfferLetters',
                    file.type,
                    true,
                );
                const uploadedUrl = await handleS3Upload(policy, file);
                if (uploadedUrl) {
                    offerLetterUrl = uploadedUrl;
                }
            } catch (error: any) {
                toast({
                    title: `Error uploading file: ${error.message}`,
                    type: 'error',
                });
                setIsLoading(false);
                return;
            }
        }

        // Submit opt-out request
        optOutMutation.mutate({
            reason: selectedReason,
            offerLetterUrl: offerLetterUrl || undefined,
        }, {
            onSettled: () => {
                setIsLoading(false);
            }
        });
    };

    // Don't show if user is Placed, VerballyPlaced, or deleted
    if (
        user?.placementStats?.status === UserPlacementTags.Placed ||
        user?.placementStats?.status === UserPlacementTags.VerballyPlaced ||
        user?.flags?.isDeletedAccount
    ) {
        return null;
    }

    return (
        <div className="bg-white dark:bg-[#1F2A37] rounded-[8px] border dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold font-inter mb-4 dark:text-white">
                Placement Opt-out
            </h2>

            {isSubmitted ? (
                <div className="text-sm md:text-base font-inter font-medium dark:text-white">
                    We are looking into it. Within 48 hours, team will reach out to you.
                </div>
            ) : !showOptions ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="text-sm md:text-base font-inter font-medium dark:text-white">
                        I would like to opt out / not interested in Masai placements.
                    </div>
                    <Button
                        color="purplePlaced"
                        onClick={() => setShowOptions(true)}
                        className="whitespace-nowrap"
                        size="sm"
                    >
                        Opt-out
                    </Button>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-4 mb-2">
                        <div className="text-sm md:text-base font-inter font-medium dark:text-white">
                            I would like to opt out / not interested in Masai placements.
                        </div>
                        <Button
                            color="gray"
                            onClick={() => {
                                setShowOptions(false);
                                setSelectedReason('');
                                setFile(null);
                            }}
                            className="whitespace-nowrap"
                            size="sm"
                        >
                            Cancel
                        </Button>
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex items-start gap-2">
                            <Radio
                                id="already_working"
                                name="optOutReason"
                                value="already_working"
                                checked={selectedReason === 'already_working'}
                                onChange={() => setSelectedReason('already_working')}
                                className="mt-[2px] cursor-pointer text-[#7777E7] focus:ring-[#7777E7]"
                            />
                            <Label htmlFor="already_working" className="cursor-pointer">
                                <span className="text-sm dark:text-[#E4E4EB] text-[#555770]">
                                    I am already working not looking for any job.
                                </span>
                            </Label>
                        </div>

                        <div className="flex items-start gap-2">
                            <Radio
                                id="got_placed"
                                name="optOutReason"
                                value="got_placed"
                                checked={selectedReason === 'got_placed'}
                                onChange={() => setSelectedReason('got_placed')}
                                className="mt-[2px] cursor-pointer text-[#7777E7] focus:ring-[#7777E7]"
                            />
                            <Label htmlFor="got_placed" className="cursor-pointer">
                                <span className="text-sm dark:text-[#E4E4EB] text-[#555770]">
                                    I just got placed and received offer letter hence would like to opt out (upload offer letter)
                                </span>
                            </Label>
                        </div>
                    </div>

                    {selectedReason === 'got_placed' && (
                        <div className="mt-1">
                            <FileInput
                                id="offer-letter-upload"
                                accept=".pdf,.doc,.docx"
                                disabled={isLoading}
                                helperText="Only PDF, DOC, DOCX files allowed. Max size: 5MB"
                                className="w-full text-xs font-normal"
                                onChange={event => {
                                    if (event.target.files && event.target.files[0]) {
                                        setFile(event.target.files[0]);
                                    }
                                }}
                            />
                        </div>
                    )}

                    <div className="flex gap-3 justify-end mt-2">
                        <Button
                            color="purplePlaced"
                            onClick={handleSubmit}
                            disabled={isLoading || optOutMutation.isLoading}
                            className="min-w-[120px]"
                            size="sm"
                        >
                            {isLoading || optOutMutation.isLoading ? <Spinner /> : 'Submit'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlacementOptOutSection;

